import { useState } from 'react';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import { SignerField } from '@/components/docusign/SignerField';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import {
  CHANGE_SIGNER_IS_VOID_AND_RESEND,
  EMPTY_SIGNER,
  SIGNER_NEEDS_EMAIL,
  isEmbeddedDelivery,
  type SignerValue,
  signerSummary,
} from '@/lib/docusign';
import type { DocuSignEnvelope } from '@/lib/types';
import { Button, ConfirmDialog, Field, Modal, Textarea } from '@/components/ui';

/*
 * "Change signer and resend."
 *
 * ── ⚠️ THERE IS NO RECIPIENT-EDIT ENDPOINT, AND THAT IS THE WHOLE DESIGN ──
 *
 * Measured in `emerge-backend-app` on 2026-08-31:
 * `/docusign/envelopes/:id/recipients` is mounted with `docusignRoutes.get(...)`
 * and there is **no `POST` and no `PATCH`** — the client's
 * `api.listDocusignRecipients` is the matching read and there is nothing else.
 * Nothing anywhere can re-point a live envelope at a different person.
 *
 * This is written down rather than assumed because the opposite was believed and
 * very nearly built: a brief for this change stated the `POST` existed and was
 * already wired. It is the absence-claim failure this codebase keeps recording,
 * arriving from the other direction — a confident POSITIVE claim about an
 * endpoint nobody had called.
 *
 * **So changing the signer is VOID-AND-RESEND**, and the sequence is:
 *
 *   1. pick the new signer, scoped to the quote's company (`SignerField`);
 *   2. write the reason the ORIGINAL signer will read;
 *   3. `POST /docusign/envelopes/{id}/void` — terminal, no undo;
 *   4. hand the new signer back so the caller opens the send dialog seeded
 *      with them.
 *
 * ── ⚠️ WHY THE SIGNER IS CHOSEN BEFORE THE VOID, NOT AFTER ───────────────
 *
 * Step 3 is irreversible and step 4 is a separate request that can fail. Asking
 * for the signer first means the confirmation can NAME the person the whole
 * exercise is for, and it means the destructive step is never taken to reach a
 * form somebody then abandons. If the send is abandoned anyway the quote is left
 * with a voided envelope and no live one — which is a real cost, stated in the
 * confirmation rather than discovered.
 *
 * ── ⚠️ THIS IS NOT "RESEND FOR SIGNATURE", AND THE TWO SIT SIDE BY SIDE ──
 *
 * A reminder leaves the envelope, the customer's signing link and any partial
 * signature untouched. This destroys all three. Anybody who wants the SAME person
 * chased wants the reminder, and the copy has to make that obvious or they will
 * void an envelope to achieve nothing — see `RemindEnvelopeControl`.
 */

const MIN_REASON = 3;
const MAX_REASON = 200;

/** The one key the void schema declares, for `routeFieldErrors`. */
const WRITE_KEYS = ['reason'] as const;

/** Names the record in a sentence. Never empty — an unnamed record is unconfirmable. */
function envelopeLabel(envelope: DocuSignEnvelope): string {
  if (envelope.quote.quoteNumber) return `quote ${envelope.quote.quoteNumber}`;
  if (envelope.quote.subject) return envelope.quote.subject;
  if (envelope.emailSubject) return envelope.emailSubject;
  return `envelope #${envelope.id}`;
}

function originalSignerName(envelope: DocuSignEnvelope): string {
  // ⚠️ This dialog voids and starts again, so on a multi-signer envelope it
  // replaces EVERY signer, not the one being named. The summary says so.
  return signerSummary(envelope);
}

export interface ChangeSignerDialogProps {
  open: boolean;
  onClose: () => void;
  envelope: DocuSignEnvelope;
  /**
   * The company whose contacts the new signer is chosen from. `null` when the
   * quote names none — the picker then offers NOTHING and says why, rather than
   * falling back to the unscoped directory of every contact in the CRM. The
   * "Someone else" path still works, which is what keeps this usable.
   */
  companyId: number | null;
  /**
   * Called after the void succeeds, with the envelope the server read back and
   * the signer to open the send dialog with.
   *
   * ⚠️ **BOTH, and the caller needs both.** The envelope replaces the row (it is
   * `voided` now); the signer is the entire point of the operation and is
   * discarded if it is not carried across — the send dialog would otherwise
   * re-seed the company's primary contact, which is very often the exact person
   * somebody has just decided not to send to.
   */
  onVoided: (envelope: DocuSignEnvelope, signer: SignerValue) => void;
}

/**
 * Unmounted while closed rather than held at `open={false}`, so every piece of
 * state is seeded on mount and there is no reset effect to forget a field in.
 * Reopening is a fresh mount — what somebody expects after cancelling out of a
 * destructive action.
 */
export function ChangeSignerDialog({ open, ...rest }: ChangeSignerDialogProps) {
  if (!open) return null;
  return <ChangeSignerForm {...rest} />;
}

function ChangeSignerForm({
  onClose,
  envelope,
  companyId,
  onVoided,
}: Omit<ChangeSignerDialogProps, 'open'>) {
  /*
   * ⚠️ Started EMPTY rather than seeded from the current signer, and that is the
   * one place this field is used that way. `SignerField` seeds the company's
   * primary contact into an untouched value — and here the primary is quite
   * possibly the person being replaced. An empty start makes the choice explicit,
   * which is what somebody opening a dialog called "change the signer" is here to
   * make.
   */
  const [signer, setSigner] = useState<SignerValue>(EMPTY_SIGNER);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [signerErrors, setSignerErrors] = useState<{ name?: string; email?: string }>({});
  const [fieldError, setFieldError] = useState<string | null>(null);
  /**
   * Everything no control can carry: a 403 (no `details` at all), an unknown-key
   * rejection, a network failure. A banner, rendered ALONGSIDE `fieldError`
   * rather than instead of it — both can be true at once.
   */
  const [formMessages, setFormMessages] = useState<string[]>([]);
  /**
   * The `409`: the envelope reached a terminal state after this screen was drawn.
   * Held apart because it withdraws the action rather than asking for a
   * correction, so the button goes rather than staying and failing.
   */
  const [refusal, setRefusal] = useState<string | null>(null);

  const label = envelopeLabel(envelope);
  const original = originalSignerName(envelope);
  /*
   * ⚠️ HOW the original signer learns depends on `delivery`. On `embedded`
   * DocuSign emails NOBODY — we hand the link over — so promising a cancellation
   * email would leave somebody waiting for a message that was never going to be
   * sent, and never telling the customer themselves. Driven by the server's own
   * field, not by a belief about it.
   */
  const emailed = !isEmbeddedDelivery(envelope);

  const trimmedReason = reason.trim();
  const reasonTooShort = trimmedReason.length < MIN_REASON;
  const reasonTooLong = trimmedReason.length > MAX_REASON;

  const newName = signer.name.trim();
  const newEmail = signer.email.trim();

  function validateSigner(): boolean {
    const found: { name?: string; email?: string } = {};
    if (!newName) found.name = 'Required.';
    /*
     * Checked here as well as in the send dialog. This dialog VOIDS before the
     * send form is ever reached, so an address that the send form would refuse
     * must not be allowed to cost somebody a live envelope first.
     */
    if (!newEmail) found.email = SIGNER_NEEDS_EMAIL;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      found.email = 'That does not look like an email address.';
    }
    setSignerErrors(found);
    return Object.keys(found).length === 0;
  }

  const canContinue = !reasonTooShort && !reasonTooLong && !submitting && !refusal;

  function submit() {
    setSubmitting(true);
    setFieldError(null);
    setFormMessages([]);

    api
      .voidDocusignEnvelope(envelope.id, { reason: trimmedReason })
      .then(({ data }) => {
        setSubmitting(false);
        setConfirming(false);
        /*
         * The trimmed pair, not the raw form value — this is what the send dialog
         * opens with and what eventually reaches DocuSign, and a trailing space in
         * a name is a recipient DocuSign matches differently.
         */
        onVoided(data, { contactId: signer.contactId, name: newName, email: newEmail });
        onClose();
      })
      .catch((caught: unknown) => {
        setSubmitting(false);
        setConfirming(false);

        // 401 belongs entirely to `api.ts`, which refreshes once and then ends
        // the session; a message here would contradict it mid sign-out.
        if (caught instanceof ApiError && caught.status === 401) return;

        if (caught instanceof ApiError && caught.status === 409) {
          /*
           * A signature completed, or somebody else voided it, since this screen
           * was drawn. The server names the state it actually found, which is the
           * only thing explaining why the button has gone, so it goes out
           * verbatim. Nothing was voided and nothing was sent.
           */
          setRefusal(caught.message);
          return;
        }

        if (caught instanceof ApiError) {
          const { fields, unroutable } = caught.routeFieldErrors(WRITE_KEYS);
          const banner = [...unroutable];
          // A 403 carries no `details`, so it yields neither a field error nor an
          // unroutable one. The server's own sentence is what stops it failing
          // silently, and it must be a banner: the role is the problem, not the
          // sentence somebody typed.
          if (!fields.reason && banner.length === 0) banner.push(caught.message);
          setFieldError(fields.reason ?? null);
          setFormMessages(banner);
          return;
        }

        setFormMessages([
          caught instanceof Error ? caught.message : 'Could not void this signature request.',
        ]);
      });
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="Change the signer"
        description={`For ${label}, currently with ${original}.`}
        size="md"
        // There is typed text in here that a clipped backdrop must not discard.
        dismissOnBackdrop={false}
        footer={
          <>
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              {refusal ? 'Close' : 'Cancel'}
            </Button>
            {/*
              Absent, not disabled, once the 409 says there is nothing to void. A
              control whose only possible outcome is an error is a control nobody
              should be shown.
            */}
            {!refusal && (
              <Button
                variant="danger"
                loading={submitting}
                disabled={!canContinue}
                onClick={() => {
                  if (!validateSigner()) return;
                  setConfirming(true);
                }}
              >
                Void and choose the document…
              </Button>
            )}
          </>
        }
      >
        <div className="grid min-w-0 gap-4">
          {/*
            ⚠️ FIRST, above the picker, because it is what somebody opening this
            dialog most likely does not know: DocuSign cannot re-point a live
            envelope, so this is destructive. Somebody who only wants the SAME
            person chased wants "Resend for signature" instead, and the last line
            says so — without it they void an envelope to achieve nothing.
          */}
          <Notice
            tone="warning"
            role="status"
            title="This replaces the request, it does not edit it"
            className="[overflow-wrap:anywhere]"
          >
            <p className="m-0">{CHANGE_SIGNER_IS_VOID_AND_RESEND}</p>
            <p className="m-0 mt-1">
              To chase <span className="font-semibold">{original}</span> instead, close this and
              use Resend for signature — that leaves everything as it is.
            </p>
          </Notice>

          {refusal && (
            <Notice
              tone="warning"
              role="alert"
              title={refusal}
              className="[overflow-wrap:anywhere]"
            >
              <p className="m-0">
                Nothing was voided and nothing was sent. Re-open the record to see where it
                stands now.
              </p>
            </Notice>
          )}

          {formMessages.length > 0 && (
            <Notice tone="danger" role="alert">
              {formMessages.map((message) => (
                <p key={message} className="m-0 min-w-0 [overflow-wrap:anywhere]">
                  {message}
                </p>
              ))}
            </Notice>
          )}

          {!refusal && (
            <>
              {/*
                The same field the send dialog uses, so the two cannot come to
                scope a company's contacts differently or word an empty list two
                ways. See `SignerField` and `@/lib/contact-scope`.
              */}
              <SignerField
                companyId={companyId}
                value={signer}
                onChange={(next) => {
                  setSigner(next);
                  setSignerErrors({});
                }}
                errors={signerErrors}
                disabled={submitting}
              />

              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  /*
                   * This dialog can be opened from inside another form. It is
                   * portalled to the body, but React propagates by the REACT
                   * tree, so an un-stopped submit reaches the form behind it and
                   * saves a half-filled record. That bug has shipped from this
                   * repo before.
                   */
                  event.stopPropagation();
                  if (!canContinue) return;
                  if (!validateSigner()) return;
                  setConfirming(true);
                }}
              >
                <Field
                  label={`Reason ${original} is shown`}
                  required
                  error={fieldError}
                >
                  {(fieldProps) => (
                    <div className="grid gap-1.5">
                      {/*
                        At the box, not in a preamble. DocuSign requires this text
                        and sends it to the ORIGINAL signer verbatim, so the person
                        typing has to know they are writing to a customer BEFORE
                        they write "wrong person".

                        Deliberately NOT the Field's `hint`: `Field` renders a hint
                        only while there is no error, so a validation failure would
                        take this warning off screen at the exact moment somebody
                        is rewriting the sentence.
                      */}
                      <div id={`${fieldProps.id}-notice`}>
                        <Notice tone="warning">
                          <p className="m-0 min-w-0 [overflow-wrap:anywhere]">
                            <span className="font-semibold">{original} will read this</span>
                            {emailed ? ', in the cancellation email' : ''} — exactly as typed.
                          </p>
                        </Notice>
                      </div>

                      <Textarea
                        {...fieldProps}
                        aria-describedby={
                          cx(fieldProps['aria-describedby'], `${fieldProps.id}-notice`) || undefined
                        }
                        value={reason}
                        onChange={(event) => {
                          setReason(event.target.value);
                          // The server judged a different string; its verdict no
                          // longer describes what is in the box.
                          setFieldError(null);
                        }}
                        invalid={Boolean(fieldError) || reasonTooLong}
                        autoGrow={{ minRows: 3, maxRows: 6 }}
                        disabled={submitting}
                      />

                      <p
                        className={cx(
                          'm-0 text-xs',
                          reasonTooLong ? 'font-medium text-danger' : 'text-fg-subtle',
                        )}
                      >
                        {reasonTooLong
                          ? `${trimmedReason.length} characters — ${trimmedReason.length - MAX_REASON} too many. DocuSign accepts ${MAX_REASON}.`
                          : `${trimmedReason.length}/${MAX_REASON} characters. At least ${MIN_REASON}.`}
                      </p>
                    </div>
                  )}
                </Field>
              </form>

              {/*
                ⛔ **`STATUS_IS_LAST_KNOWN_NOTE` was removed here — owner's
                helper-text ruling, 2026-09-05.**

                ⚠️ **The fact is unchanged and the guard that acts on it is
                untouched:** the status this screen was drawn from is only ever a
                last known value — there is no webhook, and the background worker
                calls the same refresh on a timer, so this envelope can go
                terminal with nobody having touched it. **The 409 branch above is
                what catches that, and it is a refusal, so it stays.** Removing
                the sentence removed the warning, not the handling.
              */}
            </>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={confirming}
        // Back rather than Cancel — the typed reason is still there, and
        // discarding it on a second thought would be its own small failure.
        onClose={() => setConfirming(false)}
        cancelLabel="Back"
        onConfirm={submit}
        loading={submitting}
        tone="danger"
        title={`Void ${original}’s request and start again with ${newName || 'a new signer'}?`}
        confirmLabel="Void and continue"
        description={
          <div className="grid gap-2">
            <p className="m-0">
              <span className="font-medium text-fg">{original}</span> can no longer sign{' '}
              <span className="font-medium text-fg">{label}</span>. DocuSign withdraws the request
              {emailed
                ? ' and emails them to say it was cancelled, giving this as the reason:'
                : '. Their signing link stops working, and this is the reason they are shown:'}
            </p>
            <p className="m-0 rounded-md border border-border bg-surface-sunken px-3 py-2 text-fg [overflow-wrap:anywhere]">
              “{trimmedReason}”
            </p>
            <p className="m-0">
              Any signature already on it is discarded.{' '}
              <span className="font-medium text-fg">This is final.</span>
            </p>
            {/*
              ⚠️ The second request is NOT part of this one, and saying so is the
              point. Voiding succeeds on its own; the send is a separate call that
              can be abandoned or can fail, and the quote is then left with no
              live request at all. Somebody is entitled to know that before the
              irreversible half runs.
            */}
            <p className="m-0">
              The send form opens next, for{' '}
              <span className="font-medium text-fg">{newName || 'the new signer'}</span>
              {newEmail ? (
                <>
                  {' ('}
                  <EmailLink address={newEmail} />)
                </>
              ) : null}
              .{' '}
              <span className="font-medium text-fg">Nothing is sent until you complete it</span> —
              until then this quote has no outstanding request.
            </p>
          </div>
        }
      />
    </>
  );
}
